if (!customElements.get('header-search-new')) {
	class HeaderSearchNew extends HTMLElement {
		connectedCallback() {
			this.abortController = new AbortController();
			this.requestController = null;
			this.cache = {};
			this.input = this.querySelector('[data-header-search-new-input]');
			this.form = this.querySelector('[data-header-search-new-form]');
			this.trendingState = this.querySelector('[data-header-search-new-trending]');
			this.resultsState = this.querySelector('[data-header-search-new-results]');
			this.emptyState = this.querySelector('[data-header-search-new-empty]');
			this.emptyTerm = this.querySelector('[data-header-search-new-empty-term]');
			this.tabs = this.querySelectorAll('[data-header-search-new-tab]');
			this.panels = this.querySelectorAll('[data-header-search-new-panel]');
			this.overlay = document.querySelector('.header-search-new__overflow');
			this.triggers = document.querySelectorAll('.js-header-search-trigger');
			this.headerElement = document.querySelector('shihiko-header[data-header-new]');
			this.rightMenu = this.headerElement?.querySelector('.header-new__nav-block--right');
			this.debouncedSearch = this.debounce(() => this.onQueryChange(), 280);

			if (!this.input || !this.form) return;

			const signal = this.abortController.signal;

			this.triggers.forEach((trigger) => {
				trigger.addEventListener('click', (event) => this.onTriggerClick(event, trigger), { capture: true, signal });
			});

			this.input.addEventListener('input', this.debouncedSearch, { signal });
			this.form.addEventListener('submit', () => this.syncFormAction(), { signal });
			document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
			document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
			window.addEventListener('resize', () => {
				this.syncDesktopPanelBounds();
				this.syncMobilePanelBounds();
			}, { signal });

			this.tabs.forEach((tab) => {
				tab.addEventListener('click', () => this.activateTab(tab.dataset.headerSearchNewTab), { signal });
			});
		}

		disconnectedCallback() {
			this.abortController?.abort();
			this.requestController?.abort();
		}

		debounce(callback, wait) {
			let timeout;
			return (...args) => {
				clearTimeout(timeout);
				timeout = setTimeout(() => callback.apply(this, args), wait);
			};
		}

		onTriggerClick(event, trigger) {
			event.preventDefault();
			event.stopImmediatePropagation();

			if (trigger.dataset.type === 'close' || this.classList.contains('open') && trigger.closest('header-search-new')) {
				this.close();
				return;
			}

			if (this.shouldUseExpandedHeaderMenu(trigger)) {
				this.toggleExpandedHeaderMenu();
				return;
			}

			this.open();
		}

		shouldUseExpandedHeaderMenu(trigger) {
			return window.matchMedia('(min-width: 1025px)').matches && !trigger.closest('header-search-new');
		}

		toggleExpandedHeaderMenu() {
			const shouldExpand = !this.headerElement?.classList.contains('header-new-search-expanded');

			this.closeHeaderMenus();
			this.close(false);
			this.headerElement?.classList.toggle('header-new-search-expanded', shouldExpand);

			if (shouldExpand) {
				this.syncDesktopPanelBounds();
				this.classList.add('open');
				this.setAttribute('aria-hidden', 'false');
				window.requestAnimationFrame(() => this.input.focus());
			}
		}

		syncDesktopPanelBounds() {
			if (!this.rightMenu || !window.matchMedia('(min-width: 1025px)').matches) return;

			const rect = this.rightMenu.getBoundingClientRect();
			const right = Math.max(window.innerWidth - rect.right, 0);
			const left = Math.max(rect.left, 0);

			this.style.setProperty('--header-search-new-width', `${rect.width}px`);
			this.style.setProperty('--header-search-new-left', `${left}px`);
			this.style.setProperty('--header-search-new-right', `${right}px`);
			this.style.setProperty('--header-search-new-top', `${rect.top}px`);
			this.style.setProperty('--header-search-new-menu-height', `${rect.height}px`);
		}

		syncMobilePanelBounds() {
			if (!this.headerElement || window.matchMedia('(min-width: 1025px)').matches) return;

			const header = this.headerElement.querySelector('.header-new');
			if (!header) return;

			this.style.setProperty('--header-search-new-mobile-top', `${header.getBoundingClientRect().bottom}px`);
		}

		open() {
			this.closeHeaderMenus();
			const isDesktop = window.matchMedia('(min-width: 1025px)').matches;
			if (!isDesktop) {
				this.syncMobilePanelBounds();
				this.headerElement?.classList.add('header-new-mobile-search-open');
			}
			this.classList.add('open');
			this.overlay?.classList.toggle('open', isDesktop);
			this.setAttribute('aria-hidden', 'false');
			document.body.classList.add('disable-scroll');
			window.requestAnimationFrame(() => this.input.focus());
		}

		close(collapseHeaderMenu = true) {
			this.classList.remove('open');
			this.overlay?.classList.remove('open');
			this.setAttribute('aria-hidden', 'true');
			document.body.classList.remove('disable-scroll');
			this.headerElement?.classList.remove('header-new-mobile-search-open');
			if (collapseHeaderMenu) {
				this.headerElement?.classList.remove('header-new-search-expanded');
			}
			this.requestController?.abort();
		}

		closeHeaderMenus() {
			document.querySelector('#header-mobile-menu.header-new__mobile-menu')?.classList.remove('open');
			const mobileMenuButton = document.querySelector('.header-new__mobile-menu-button');
			mobileMenuButton?.classList.remove('open');
			mobileMenuButton?.setAttribute('aria-expanded', 'false');
			mobileMenuButton?.setAttribute('aria-label', 'Open mobile menu');
			document.querySelector('shihiko-header[data-header-new]')?.classList.remove('header-new-mobile-menu-open');
			document.body.classList.remove('disable-scroll');
			document.querySelector('.js-header-menu-overflow')?.classList.remove('open');
			document.querySelectorAll('.header-new .open-submenu').forEach((item) => {
				item.classList.remove('open-submenu');
				item.querySelector('[aria-expanded="true"]')?.setAttribute('aria-expanded', 'false');
			});
		}

		onKeydown(event) {
			if (event.key === 'Escape' && (this.classList.contains('open') || this.headerElement?.classList.contains('header-new-search-expanded'))) {
				this.close();
			}
		}

		onDocumentClick(event) {
			if (!(event.target instanceof Element)) return;
			if (!this.headerElement?.classList.contains('header-new-search-expanded')) return;
			if (event.target.closest('shihiko-header[data-header-new]')) return;
			if (event.target.closest('header-search-new')) return;

			this.close();
		}

		activateTab(tabId) {
			this.tabs.forEach((tab) => {
				const active = tab.dataset.headerSearchNewTab === tabId;
				tab.classList.toggle('is-active', active);
				tab.setAttribute('aria-selected', active ? 'true' : 'false');
			});

			this.panels.forEach((panel) => {
				panel.classList.toggle('is-active', panel.dataset.headerSearchNewPanel === tabId);
			});
		}

		onQueryChange() {
			const query = this.getQuery();

			if (!query.length) {
				this.showState('trending');
				this.resultsState.innerHTML = '';
				return;
			}

			this.fetchResults(query);
		}

		getQuery() {
			return this.input.value.trim();
		}

		fetchResults(query) {
			const cacheKey = query.toLowerCase();

			if (this.cache[cacheKey]) {
				this.renderResults(this.cache[cacheKey], query);
				return;
			}

			this.requestController?.abort();
			this.requestController = new AbortController();

			const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search-new`;

			fetch(url, { signal: this.requestController.signal })
				.then((response) => {
					if (!response.ok) throw new Error(response.status);
					return response.text();
				})
				.then((text) => {
					this.cache[cacheKey] = text;
					this.renderResults(text, query);
				})
				.catch((error) => {
					if (error.name !== 'AbortError') {
						this.showEmpty(query);
					}
				});
		}

		renderResults(markup, query) {
			const doc = new DOMParser().parseFromString(markup, 'text/html');
			const resultRoot = doc.querySelector('#predictive-search-new-results');
			const productResults = resultRoot?.querySelector('[data-header-search-new-result-products]');

			if (!productResults || !productResults.children.length) {
				this.showEmpty(query);
				return;
			}

			this.resultsState.innerHTML = resultRoot.innerHTML;
			this.showState('results');
			this.refreshProductCardScripts();
		}

		showEmpty(query) {
			if (this.emptyTerm) {
				this.emptyTerm.textContent = query;
			}
			this.resultsState.innerHTML = '';
			this.showState('empty');
		}

		showState(state) {
			this.trendingState?.classList.toggle('is-active', state === 'trending');
			this.resultsState?.classList.toggle('is-active', state === 'results');
			this.emptyState?.classList.toggle('is-active', state === 'empty');
		}

		syncFormAction() {
			this.input.value = this.getQuery();
		}

		refreshProductCardScripts() {
			if (typeof window.reloadProductCardHover === 'function') {
				window.reloadProductCardHover();
			}
		}
	}

	customElements.define('header-search-new', HeaderSearchNew);
}
